import React from 'react';
import styled from 'styled-components';

// 定义 Pagination 组件的 props 接口
interface PaginationProps {
  currentPage: number;        // 当前页码
  totalItems: number;         // 总条目数
  itemsPerPage: number;       // 每页显示的条目数
  onPageChange: (page: number) => void; // 页码改变时的回调函数
  pageRangeDisplayed?: number; // 可选：显示在当前页码周围的页码数量（不包括省略号和首尾页）
  alignment?: 'left' | 'center' | 'right'; // 新增：分页组件的对齐方式，默认为 'right'
}


const Button = styled.button<{active?: boolean, alignment?: string}>`
  background-color: transparent;
  border: 1px solid ${({ theme ,active}) =>active ? theme.primary1 : theme.text2};
  color: ${({ theme,active }) => active ? theme.primary1 : theme.text1};
  min-width: 24px;
  min-height: 24px;
  padding: 4px 4px;
  border-radius: 2px;
  cursor: pointer;
  margin-right: 8px;
//   margin:${({ alignment }) => alignment == 'flex-end'?'0px 0px 0px 8px':'0px 8px 0px 0px' } ;
//   :disabled {
//     border: 1px solid ${({ theme }) => 'red'};
//     color: ${({ theme }) => 'red'};
//   }
 @media only screen and (min-width: 960px) {
    min-width: 32px;
    min-height: 32px;
    padding: 4px 4px;
    border-radius: 4px;
  }
`;

const RowView = styled.div<{ alignment?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin: 12px 0px;
  padding-right:-8px;//${({ alignment }) => (!alignment || alignment =='flex-end')? '-8px':'0px' } ;
  justify-content:${({ alignment }) => alignment || 'flex-end '};
`;


const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  pageRangeDisplayed = 3, // 默认显示当前页码周围的3个页码
  alignment = 'right',   // 默认靠右显示
}) => {
  // 计算总页数
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // 如果总页数小于等于1，则不显示分页组件
  if (totalPages <= 1) {
    return null;
  }

 // 生成要显示的页码数组
  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const maxVisibleMiddlePages = 3; // 用户要求：中间最多显示3个页码

    // Case 1: 总页数很小，直接显示所有页码，无需省略号
    // 如果总页数 <= (第一页 + 中间最多3页 + 最后一页) + 2 (为了避免省略号出现，比如 1 ... 5 6 7 ... 10)
    // 也就是 1 + 3 + 1 = 5。如果总页数 <= 5，就显示所有。
    // 更通用的判断：如果总页数少于或等于 (中间可见页数 + 2 * 两侧边界页数 + 2 * 省略号位置)
    // 假设两侧边界页数是 1 (即第一页和最后一页)，省略号位置是 1
    // 那么总共需要 1 (第一页) + 1 (左侧省略号) + 3 (中间) + 1 (右侧省略号) + 1 (最后一页) = 7 个位置
    // 所以如果总页数小于等于 7，就显示所有页码。
    if (totalPages <= maxVisibleMiddlePages + 4) { // 3 (中间) + 1 (第一页) + 1 (最后一页) + 2 (两个省略号位置) = 7
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
      return pageNumbers;
    }

    // 总是显示第一页
    pageNumbers.push(1);

    // 计算中间页码块的起始和结束页码
    // 默认以当前页为中心，左右各显示 floor(maxVisibleMiddlePages / 2) 个页码
    let startMiddlePage = currentPage - Math.floor(maxVisibleMiddlePages / 2);
    let endMiddlePage = currentPage + Math.floor(maxVisibleMiddlePages / 2);

    // 调整中间页码块，使其不超出边界
    // 如果当前页码靠近开头 (导致 startMiddlePage 小于 2)
    if (startMiddlePage < 2) {
      startMiddlePage = 2; // 从第 2 页开始
      endMiddlePage = startMiddlePage + maxVisibleMiddlePages - 1; // 扩展结束页码以保持中间页数
    }

    // 如果当前页码靠近结尾 (导致 endMiddlePage 大于 totalPages - 1)
    if (endMiddlePage > totalPages - 1) {
      endMiddlePage = totalPages - 1; // 结束于倒数第 2 页
      startMiddlePage = endMiddlePage - maxVisibleMiddlePages + 1; // 调整起始页码以保持中间页数
      // 再次检查，确保 startMiddlePage 不会小于 2 (防止在总页数较少时出现负数或 0)
      if (startMiddlePage < 2) {
        startMiddlePage = 2;
      }
    }

    // 添加左侧省略号
    // 如果中间页码块的起始页码不是第 2 页，则需要左侧省略号
    if (startMiddlePage > 2) {
      pageNumbers.push('...');
    }

    // 添加中间页码
    for (let i = startMiddlePage; i <= endMiddlePage; i++) {
      pageNumbers.push(i);
    }

    // 添加右侧省略号
    // 如果中间页码块的结束页码不是倒数第 2 页，则需要右侧省略号
    if (endMiddlePage < totalPages - 1) {
      pageNumbers.push('...');
    }

    // 总是显示最后一页
    pageNumbers.push(totalPages);

    // 过滤掉连续的省略号 (作为安全措施，理论上前面的逻辑应该避免这种情况)
    return pageNumbers.filter((value, index, self) => {
      if (value === '...' && index > 0 && self[index - 1] === '...') {
        return false;
      }
      return true;
    });
  };

  const pageNumbersToDisplay = getPageNumbers();

  // 处理页码点击事件
  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page !== currentPage) {
      onPageChange(page);
    }
  };

  // 处理上一页点击事件
  const handlePrevClick = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  // 处理下一页点击事件
  const handleNextClick = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // 根据 alignment prop 设置 justify-content
  const alignmentClass = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end ',
  }[alignment];

  return (
    <RowView alignment={alignmentClass}>
      {/* 上一页按钮 */}
      <Button
        onClick={handlePrevClick}
        disabled={currentPage === 1}
      >
        {"<"}
      </Button>

      {/* 页码列表 */}
      {pageNumbersToDisplay.map((page, index) => (
        <Button
          key={index} // 使用索引作为 key，因为页码可能重复（省略号）
          onClick={() => handlePageClick(page)}
          disabled={page === '...'} // 省略号按钮不可点击
          active={page === currentPage}
        >
          {page}
        </Button>
      ))}
      <Button onClick={handleNextClick} disabled={currentPage === totalPages}>
           { ">"}
      </Button>
    </RowView>
  );
};

export default Pagination;
